import React, { useEffect, useState } from "react";
import TableRow from "./TableRow";
import InputTableRow from "./InputTableRow";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableRow from "./SortableRow";

export default function TableBody({
  currentData,
  columns,
  onPopoverToggle,
  openPopover,
  handlePopoverAction,
  module,
  showEmptyRow,
  onSave,
  onCancel,
  optionData,
  draggable,
  onReorder,
  onRowChange,
  onRowClick,
  isRowClickable,
}) {
  const getRowId = (row) => {
    if (module === "attendance") {
      return row.childId;
    }
    return row.id || row.familyId || row._id;
  };
  const [items, setItems] = useState(currentData.map(getRowId));

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setItems(currentData.map(getRowId));
  }, [currentData]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);

      // Reorder the original data based on newItems order
      const reorderedData = newItems.map((id) =>
        currentData.find((row) => getRowId(row) === id)
      );

      // Call onReorder with reordered data
      if (onReorder) {
        onReorder(reorderedData);
      }
    }
  };

  if (draggable) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <tbody>
          {showEmptyRow && (
            <InputTableRow
              onSave={onSave}
              onCancel={onCancel}
              optionData={optionData}
              hasRows={currentData.length > 0}
              module={module}
              draggable={draggable}
              colSpan={columns.length + (draggable ? 1 : 0) + 1}
            />
          )}
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            {items.map((id) => {
              const row = currentData.find((r) => getRowId(r) === id);
              if (!row) return null;
              return (
                <SortableRow
                  key={id}
                  id={id}
                  row={row}
                  columns={columns}
                  onPopoverToggle={onPopoverToggle}
                  openPopover={openPopover}
                  handlePopoverAction={handlePopoverAction}
                  module={module}
                  onRowChange={onRowChange}
                  onRowClick={onRowClick}
                  isRowClickable={isRowClickable}
                />
              );
            })}
          </SortableContext>
        </tbody>
      </DndContext>
    );
  }
  return (
    <tbody>
      {showEmptyRow && (
        <InputTableRow
          onSave={onSave}
          onCancel={onCancel}
          optionData={optionData}
          hasRows={currentData.length > 0}
          module={module}
          onRowClick={onRowClick}
          colSpan={columns.length + 1}
        />
      )}
      {currentData.length > 0 ? (
        currentData.map((row) => {
          const rowId = getRowId(row);
          return (
            <TableRow
              key={rowId}
              row={row}
              rowId={rowId}
              columns={columns}
              onPopoverToggle={onPopoverToggle}
              openPopover={openPopover}
              handlePopoverAction={handlePopoverAction}
              module={module}
              onRowChange={onRowChange}
              onRowClick={onRowClick}
              isRowClickable={isRowClickable}
            />
          );
        })
      ) : !showEmptyRow ? (
        <tr>
          <td colSpan={columns.length} style={{ textAlign: "center" }}>
            {module === "parent"
              ? "No Parents Assigned"
              : module === "child"
              ? "No Children Assigned"
              : module === "family"
              ? "No Family Assigned"
              : module === "notifications"
              ? "No Campaign Assigned"
              : module === "associatedStudent"
              ? "No Student Assigned"
              : "No Matching Data Found"}
          </td>
        </tr>
      ) : null}
    </tbody>
  );
}
